import _ from "lodash";
import withAsyncRouteLoading from "UIComponents/AsyncRouteLoadingHoc";
import { injectReducer } from "store/reducers";

import SchoolInformation from "./routes/SchoolInformation";
import GradeLevels from "./routes/GradeLevels";
import AcademicYears from "./routes/AcademicYears";
import Classes from "./routes/Classes";
import TeacherSetup from "./routes/TeacherSetup";
import StudentSetup from "./routes/StudentSetup";
import TeacherCourses from "./routes/TeacherCourses";
import BlueprintCourses from "./routes/BlueprintCourses";
import GradeSetup from "./routes/GradeSetup";
import ModuleOnOff from "Administrator/routes/SchoolSettings/routes/ModuleSettings";
import ModuleSettingsV2 from "Administrator/routes/SchoolSettings/routes/ModuleSettingsV2";
import CourseAndClassModuleSettings from "Administrator/routes/SchoolSettings/routes/CourseAndClassModuleSettings/entries/school";
import GlobalModuleSettings from "Administrator/routes/SchoolSettings/routes/GlobalModuleSettings/entries/school";
import OtherModuleSettingsTab from "Platform/routes/AdminSettings/routes/OtherModuleSettingsTab";
import PortfolioSettingsTab from "Platform/routes/AdminSettings/routes/PortfolioSettingsTab";
import GeneralAcademicSetup from "./routes/GeneralAcademicSetup";
import UnitPlanPdfDownloadTool from "Administrator/routes/SchoolSetup/routes/UnitPlanPdfDownloadTool";
import Trash from "Administrator/routes/Trash";
import YearGroupManager from "./routes/YearGroupManager";
import Sync from "Administrator/routes/SchoolSetup/routes/Sync";
import SyncV2 from "SchoolSetup/routes/SyncV2";
import CourseCodes from "./routes/CourseCodes";

import {
  AttendanceSettingsV2,
  AttendanceAndTimetableSettings,
  TimetableManagerV2,
} from "Administrator/routes";
import Timetable from "Administrator/routes/Timetable";
import NotificationsManager from "Administrator/routes/NotificationsManager";
import ManageFamily from "Course/routes/ManageFamily";
import AdminEducatorCenterV2 from "Administrator/routes/AdminEducatorCenterV2";
import SubjectsSetup from "Administrator/routes/AcademicSetup/routes/SubjectsSetup";
import SubjectGroupsAndSubjects from "Administrator/routes/MYPAcademicSetup/routes/SubjectGroupsAndSubjects";
import {
  DatasetsAndTemplatesRoute,
  GradingSetupRoute,
  ReportingSetupRoute,
  DPSubjectsRoute,
  SchoolOperationsRoute,
  GradingSetupV2Route,
  SettingsRoute,
  LevelsRoute,
  IntegrationsRoute,
  StudentTransfersRoute,
} from "AppComponents/AdminNavigation";
import AdminScopeAndSequence from "Administrator/routes/AcademicSetup/routes/AdminScopeAndSequence";
import UBDLearningStandards from "UBDAcademicSetup/routes/LearningStandards";
import MYPLearningStandards from "Administrator/routes/MYPAcademicSetup/routes/LearningStandards";
import GradingPeriods from "Administrator/commonComponents/GradingPeriods";
import GradingPeriodsV3 from "Administrator/commonComponents/GradingPeriodsV3";
import SubjectStandardsV2 from "Administrator/commonComponents/SubjectStandardsV2";
import Datasets from "Datasets";
import DPLearningStandards from "Administrator/routes/DPAcademicSetup/routes/DPLearningStandards";
import { ExamRegistration } from "Administrator/routes/DPExamRegistration";
import IBISRegistration from "Administrator/routes/IBISRegistration";
import { ECoursework } from "Administrator/routes/IB_ECoursework";

import { clearAdminNavigationState } from "Utils/AdminNavigationUtils/AdminNavigationModule";

import Departments from "Platform/routes/AcademicPortal/routes/Departments";
import SubjectsSetupV2 from "Platform/routes/AcademicPortal/routes/SubjectsSetupV2";
import AdminGradeBook from "Administrator/routes/AdminGradebook";
import PlanningInsights from "PlanningInsights";
import YearGroup from "Administrator/routes/YearGroup";
import RoutineAndMealTemplatesRoute from "Platform/routes/AcademicPortal/routes/RoutinesAndMealTemplates";
import CasExternalSupervisorSetup from "SchoolSetup/routes/CasExternalSupervisorSetup";
import AcademicYearTransition from "SchoolSetup/routes/AcademicYearTransition";
import ExternalAssessmentSetup from "ExternalAssessmentSetup";
import {
  ExternalAssessmentRecordsRoute,
  ExternalAssessmentTemplatesRoute,
} from "ExternalAssessments";
import TranscriptV2 from "TranscriptsV2";
import TranscriptsV3 from "TranscriptsV3";

// Import Terms from the shared location
import Terms from "Platform/components/Terms";

export default store => ({
  path: "school/:schoolId",
  component: withAsyncRouteLoading(() =>
    Promise.all([
      import(
        /* webpackChunkName: "administrator-curriculum-type" */ "./components"
      ),
      import(
        /* webpackChunkName: "administrator" */ "Administrator/modules/AdministratorModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/SchoolSetupModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "ResetInviteAndSignInCodeModal/modules/ResetInviteAndSignInCodeModalModule"
      ),
      import(
        /* webpackChunkName: "admin-navigation" */ "Utils/AdminNavigationUtils/AdminNavigationModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/StaffEnrollModalModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "TeacherSetup/components/BulkActions/modules/BulkActionsModule"
      ),
      import(
        /* webpackChunkName: "admin-schoolSetup" */ "SchoolSetup/modules/StudentEnrollModalModule"
      ),
    ]).then(
      ([
        importedModule0,
        importedModule1,
        importedModule2,
        importedModule3,
        importedModule4,
        importedModule5,
        importedModule6,
        importedModule7,
      ]) => {
        const component = importedModule0.default;
        const reducer = importedModule1.default;
        const SchoolSetupModule = importedModule2.default;
        const bulkActionsModule = importedModule6;
        const studentEnrollModalModule = importedModule7.default;
        const resetInviteAndSignInCodeModalModule = importedModule3.default;
        injectReducer(store, {
          key: "administrator",
          reducer,
        });
        injectReducer(store, {
          key: "schoolSetup",
          reducer: SchoolSetupModule,
        });

        injectReducer(store, {
          key: "resetInviteAndSignInCodeModal",
          reducer: resetInviteAndSignInCodeModalModule,
        });
        injectReducer(store, {
          key: "studentEnrollModal",
          reducer: studentEnrollModalModule,
        });

        // Inject the AdminNavigation reducer
        injectReducer(store, {
          key: "adminNavigation",
          reducer: importedModule4.default,
        });

        // Inject the StaffEnrollModal reducer
        injectReducer(store, {
          key: "staffEnrollModal",
          reducer: importedModule5.default,
        });

        // inject the bulkActionsModule reducer
        injectReducer(store, {
          key: bulkActionsModule.NAME,
          reducer: bulkActionsModule.default,
        });

        return component;
      }
    )
  ),
  childRoutes: [
    SchoolInformation(store),
    GradeLevels(store),
    AcademicYears(store),
    TeacherSetup(store),
    Classes(store),
    StudentSetup(store),
    AttendanceSettingsV2(store),
    AttendanceAndTimetableSettings(store),
    CourseCodes(store),
    StudentTransfersRoute(store),
    TeacherCourses(store),
    BlueprintCourses(store),
    SettingsRoute(store),
    ModuleOnOff(store),
    ModuleSettingsV2(store),
    CourseAndClassModuleSettings(store),
    GlobalModuleSettings(store),
    OtherModuleSettingsTab(store),
    PortfolioSettingsTab(store),
    GeneralAcademicSetup(store),
    Timetable(store),
    TimetableManagerV2(store),
    NotificationsManager(store),
    UnitPlanPdfDownloadTool(store),
    ManageFamily(store),
    Departments(store),
    Trash(store),
    AdminEducatorCenterV2(store),
    SubjectsSetup(store),
    SubjectGroupsAndSubjects(store),
    DatasetsAndTemplatesRoute(store),
    GradingSetupRoute(store),
    ReportingSetupRoute(store),
    DPSubjectsRoute(store),
    AdminScopeAndSequence(store),
    UBDLearningStandards(store),
    MYPLearningStandards(store),
    GradingPeriods(store),
    GradingPeriodsV3(store),
    GradeSetup(store),
    YearGroupManager(store),
    // Add Terms route to SchoolPortal
    Terms(store),
    SchoolOperationsRoute(store),
    SubjectStandardsV2(store),
    Datasets(store),
    GradingSetupV2Route(store),
    LevelsRoute(store),
    SubjectsSetupV2(store),
    DPLearningStandards(store),
    ..._.map(ECoursework, route => route(store)),
    ..._.map(ExamRegistration, route => route(store)),
    IBISRegistration(store),
    AdminGradeBook(store),
    PlanningInsights(store),
    Sync(store),
    SyncV2(store),
    IntegrationsRoute(store),
    YearGroup(store),
    RoutineAndMealTemplatesRoute(store),
    CasExternalSupervisorSetup(store),
    TranscriptV2(store),
    TranscriptsV3(store),
    AcademicYearTransition(store),
    ExternalAssessmentSetup(store),
    ExternalAssessmentTemplatesRoute(store),
    ExternalAssessmentRecordsRoute(store),
  ],
  onLeave: () => {
    /**
     * This is required to ensure that new route does not
     * access old redux state setup by current route during 1st render.
     */
    store.dispatch(clearAdminNavigationState());
  },
});
